import { AlertTriangle, Zap } from "lucide-react";
import type { RequisitionAutoLineItem } from "../types";

/** Wiersze, które automat dopisze (albo dopisałby, gdyby nie pominięcie) — te zatwierdzający
 *  może przełączyć. Pozycje już będące wierszem zapotrzebowania widać w tabeli pozycji. */
export function toggleableAutoRows(rows: RequisitionAutoLineItem[] | null | undefined): RequisitionAutoLineItem[] {
  return (rows ?? []).filter((r) => !r.on_requisition && (r.will_be_added || r.declined));
}

/**
 * Pozycje automatyczne umowy ramowej, które dotyczą zapotrzebowania (ich wyzwalacz jest na
 * liście) — co dopisze się do zamówienia samo przy zatwierdzeniu, a co nie i dlaczego.
 * Bez `onToggle` tylko informacja (szczegół zapotrzebowania); z `onToggle` zatwierdzający
 * odznacza pozycję, której zamawiający nie chce (overrides.declined_auto_line_item_types).
 */
export function RequisitionAutoLineItems({
  rows,
  declined,
  onToggle,
  className = "",
}: {
  rows: RequisitionAutoLineItem[] | null | undefined;
  /** Bieżąca lista pominiętych (id) — w oknie zatwierdzania stan lokalny, inaczej z serwera. */
  declined: ReadonlySet<string>;
  onToggle?: (id: string) => void;
  className?: string;
}) {
  const visible = (rows ?? []).filter((r) => !r.on_requisition);
  if (visible.length === 0) return null;
  const adding = visible.filter((r) => (r.will_be_added || r.declined) && !declined.has(r.id));
  return (
    <div
      data-testid="requisition-auto-line-items"
      className={`rounded border px-3 py-2 text-xs ${
        adding.length
          ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
          : "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300"
      } ${className}`}
    >
      <p className="mb-1.5 flex items-center gap-1.5 font-semibold">
        {adding.length ? <AlertTriangle size={13} className="shrink-0" /> : <Zap size={13} className="shrink-0" />}
        {adding.length
          ? "Przy zatwierdzeniu do zamówienia dopiszą się automatycznie pozycje z umowy ramowej:"
          : "Pozycje automatyczne umowy ramowej — żadna nie dopisze się do zamówienia:"}
      </p>
      <ul className="space-y-1">
        {visible.map((r) => {
          const toggleable = r.will_be_added || r.declined;
          const included = toggleable && !declined.has(r.id);
          const trigger = r.trigger_names.length ? r.trigger_names.map((n) => `„${n}”`).join(", ") : "";
          return (
            <li key={r.id} className="flex items-start gap-2" data-testid="requisition-auto-line-item">
              {onToggle && toggleable ? (
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={included}
                  onChange={() => onToggle(r.id)}
                  aria-label={`Dopisz „${r.name}” do zamówienia`}
                />
              ) : (
                <Zap size={12} className="mt-0.5 shrink-0 opacity-60" />
              )}
              <span className={included ? "" : "opacity-70"}>
                <span className="font-medium">{r.name}</span>
                {trigger && <> (przy {trigger})</>} — {r.pricing}.{" "}
                {included ? (
                  <span>Dopisze się.</span>
                ) : toggleable ? (
                  <span>Pominięta — nie dopisze się do zamówienia.</span>
                ) : (
                  <span>Nie dopisze się: {r.skip_reason}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {onToggle && toggleableAutoRows(visible).length > 0 && (
        <p className="mt-1.5 opacity-80">Odznacz pozycję, której zamawiający nie chce — decyzja zostanie zapisana na zapotrzebowaniu.</p>
      )}
    </div>
  );
}
